<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2022-2024, Vector Software BV                             **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Vector Software BV. Unauthorized copying or disclosure of this file,    **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
*****************************************************************************
-->

<template>
    <v-dialog
        @click:outside="onClose()"
        v-model="dialog"
        id="notificationCenter"
        width="600px"
        :fullscreen="$vuetify.breakpoint.xsOnly"
    >
        <v-card :loading="loadingNotifications || loadingDeviceName">
            <v-card-title class="py-6">
                <div
                    class="text-h6 text-sm-h4"
                    style="word-break: break-word;"
                >
                    {{ language.t("Vector.NotificationCenter.title") }}
                </div>
            </v-card-title>
            <v-card-text>
                {{ deviceName }}
                <v-divider></v-divider>
                <v-data-table
                    fixed-header
                    :headers="headers"
                    :items="notifications"
                    :items-per-page="10"
                    :style="$vuetify.breakpoint.xsOnly ? 'max-height:calc(100vh - 180px)' : 'max-height:calc(90vh - 180px)'"
                    class="overflow-y-auto"
                    :footer-props="{
                        itemsPerPageText: language.t('DataTables.itemsPerPageText'),
                        itemsPerPageAllText: language.t('DataTables.itemsPerPageAllText'),
                        itemsPerPageOptions: [10, 25, 100, -1],
                    }"
                >
                    <template v-slot:item.accountactivityaction="{ item }">
                        <v-chip
                            :color="item.color"
                            dark
                            small
                        >
                            {{ item.status }}
                        </v-chip>
                    </template>
                    <template v-slot:item.description="{ item }">
                        <div style="white-space: pre-wrap;">{{ item.description }}</div>
                    </template>
                    <template v-slot:[`footer.page-text`]="items">
                        {{ items.pageStart }} - {{ items.pageStop }}
                        {{ language.t("DataTables.pageText") }} {{ items.itemsLength }}
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions class="card-actions">
                <v-spacer></v-spacer>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="onClose"
                >
                    <v-icon>mdi-close</v-icon>{{ language.t('App.close') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { nothing } from 'ol/pixel';

export default {
    components: {
    },
    data() {
        return {
            dialog: true,
            notifications: [],
            deviceName: "loading",
            loadingDeviceName: false,
            loadingNotifications: false,

            headers: [
                { text: 'Status', sortable: false, value: 'accountactivityaction' },
                {
                    text: 'Description',
                    align: 'start',
                    sortable: false,
                    value: 'description',
                },
                { text: 'Timestamp', sortable: false, value: 'timestamp' },
            ],
        };
    },
    mounted: function () {
        this.loadingNotifications = true;
        this.loadingDeviceName = true;
        this.$axios.get(route('api.devices.statuses', this.$selectedVector.accountId)).then((res) => {
            res.data.forEach(item => {
                var notif = null;


                switch (item.accountactivityaction) {
                    case "STATUS":
                        notif = this.parseStatus(item);
                        break;
                    case "WARNING":
                        notif = this.parseWarning(item);
                        break;
                    case "ERROR":
                        notif = this.parseError(item);
                        break;
                    default:
                        break;
                }


                this.notifications.push(notif);
            });
            this.loadingNotifications = false;
        });

        this.$axios.get(route('api.devices.single', this.$selectedVector.accountId)).then((result) => {
            this.deviceName = result.data.designator ? result.data.designator : result.data.deviceSerialNumberComplete;
            this.loadingDeviceName = false;
        });
    },
    computed: {
        customerId() {
            return this.$auth.customerId;
        }
    },
    methods: {
        onClose() {
            this.$emit("close");
        },
        parseStatus(item) {
            var status = item.description.split(",")[0];
            var workingTask = null;
            var color = 'grey lighten-1';
            var description = ""

            switch (status) {
                case "WORKING":
                    workingTask = item.description.split(",")[1];
                    color = 'green';
                    description = this.language.t("Vector.NotificationCenter.WORKING");
                    break;
                case "READY":
                    workingTask = item.description.split(",")[1];
                    description = this.language.t("Vector.NotificationCenter.READY");
                    break;
                case "IDLE":
                    description = this.language.t("Vector.NotificationCenter.IDLE");
                    break;
                case "SHUTDOWN":
                    description = this.language.t("Vector.NotificationCenter.SHUTDOWN");
                    break;
                case "RESTART":
                    description = this.language.t("Vector.NotificationCenter.RESTART");
                    break;
                default:
                    break;
            }

            return {
                status: status,
                accountactivityaction: item.accountactivityaction,
                description: description,
                timestamp: item.timestamp,
                color: color
            }
        },
        parseWarning(item) {
            var warnings = item.description.split(",");
            warnings.shift();
            var description = "";
            var description2 = "";
            warnings.forEach(warning => {
                switch (warning) {
                    case "OBSTACLE":
                        description2 = this.language.t("Vector.NotificationCenter.OBSTACLE");
                        break;
                    default:
                        break;
                }
            });
            if (description && !description2) {
                description += ")."
            } else if (description) {
                description += ')'
            }

            if (description && description2) {
                description += (" " + this.language.t("Vector.NotificationCenter.and") + " " + description2.toLowerCase());
            } else if (description2) {
                description = description2;
            }

            return {
                status: item.accountactivityaction,
                accountactivityaction: item.accountactivityaction,
                description: description,
                timestamp: item.timestamp,
                color: "orange"
            }
        },
        parseError(item) {
            var errors = item.description.split(",");
            errors.shift(); //ERROR
            errors.shift(); //TASKID
            var description = "";
            errors.forEach(error => {
                if (description) description += "\r\n";
                switch (error) {

                    case "OBSTACLE":
                        description += "- " + this.language.t("Vector.Errors.obstacle")
                        break;
                    case "OBSTACLESTOPPED":
                        description += "- " + this.language.t("Vector.Errors.obstacleStopped")
                        break;
                    case "BUMPER":
                        description += "- " + this.language.t("Vector.Errors.safetyBumper")
                        break;
                    case "RC-ESWITCH":
                        description += "- " + this.language.t("Vector.Errors.rcESwitch")
                        break;
                    case "ESWITCH-RIGHT":
                        description += "- " + this.language.t("Vector.Errors.eSwitchRight")
                        break;
                    case "ESWITCH-LEFT":
                        description += "- " + this.language.t("Vector.Errors.eSwitchLeft")
                        break;
                    case "LIVEEDGE":
                        description += "- " + this.language.t("Vector.Errors.liveEdge")
                        break;
                    case "RC-OUT_OF_RANGE":
                        description += "- " + this.language.t("Vector.Errors.rcOutOfRange")
                        break;
                    case "RC-OFF":
                        description += "- " + this.language.t("Vector.Errors.rcOff")
                        break;
                    default:
                        if (description) description -= "\r\n";
                        console.log('unknown error: ' + error);
                        break;
                }

            });

            return {
                status: item.accountactivityaction,
                accountactivityaction: item.accountactivityaction,
                description: description,
                timestamp: item.timestamp,
                color: "red"
            }
        }
    }
};
</script>
<style>
#error.row {
    color: red;
}

#warning.row {
    color: rgb(255, 115, 0);
}
</style>